import React from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ISanityImage } from '@rbi-ctg/menu';
import Modal, { ModalContent, ModalHeading, ModalSize } from 'components/modal';
import { Allergens } from 'components/nutrition/allergens';
import { AllergenGroup } from 'components/nutrition/types';
import { getAllergenGroups } from 'components/nutrition/utils';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { formatCalories } from 'utils/menu';
import { units } from 'utils/menu/units';

import { Card, Title } from './card';
import { IProduct, ISwap } from './types';

const Content = styled(ModalContent)`
  background-color: ${Styles.color.background};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: ${Styles.layout.smallerSectionMaxWidth};
  padding-bottom: 1.5rem;
`;

const Header = styled(ModalHeading)`
  margin: 0;
  margin-bottom: 0.3rem;

  ${Styles.desktop`
    margin-bottom: 0.15rem;
  `}
`;

const Subtitle = styled.h4`
  margin: 0;
  margin-bottom: 2.5rem;
  line-height: 1rem;

  text-transform: ${Styles.textTransform.headlines};
  font-family: ${Styles.fontFamily.base};
  font-size: 0.8rem;
  font-weight: ${Styles.fontWeight.light};
  text-align: center;

  ${Styles.desktop`
    margin-bottom: 1rem;
    font-size: 1.2rem;
    line-height: inherit;
  `}
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;

  padding-bottom: 1.5rem;
  & > * + * {
    margin: 0;
    margin-top: 1.5rem;
  }

  ${Styles.desktop`
    flex-direction: row;
    padding: 0;
    & > * + * {
      margin: 0;
      margin-left: 1rem;
    }
  `}
`;

const StyledCard = styled(Card)<{ isSelected?: boolean }>`
  flex: 1;
  border: ${p => p.isSelected && `solid 1px ${Styles.color.primary}`};

  /* Until the swaps are selectable, don't show a hover state or cursor */
  cursor: auto;
  &:hover {
    box-shadow: ${Styles.boxShadow};
  }
`;

const CenteredCardTitle = styled(Title)`
  text-align: center;
`;

const CardContentContainer = styled.div`
  width: 100%;
  padding: 0.25rem 0.2rem 0;
`;

const NutritionInfoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 1.2rem;
  padding: 0 6px;
`;

const NutritionInfoItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-family: ${Styles.fontFamily.body};
`;

const ItemValue = styled.p`
  margin: 0;
  font-size: 0.75rem;
  font-weight: bold;
`;

const ItemName = styled.p`
  margin: 0;
  font-size: 0.7rem;
  text-transform: capitalize;
`;

const NutritionInfoItem: React.FC<{ name: string; value: string }> = ({ name, value }) => (
  <NutritionInfoItemContainer>
    <ItemValue>{value}</ItemValue>
    <ItemName>{name}</ItemName>
  </NutritionInfoItemContainer>
);

const AllergenInfoTitle = styled.h5`
  margin: 1.1rem 0 0.1rem;
  font-size: 0.85rem;
  font-weight: 400;
  font-family: ${Styles.fontFamily.base};
  text-transform: uppercase;
`;

const CardContent: React.FC<{ product: IProduct }> = ({ product }) => {
  const { formatMessage } = useIntl();
  const { region } = useLocale();
  const enableAllergens = useFlag(LaunchDarklyFlag.ENABLE_NUTRITION_EXPLORER_ALLERGENS);

  const calories = formatCalories(product.nutrition.calories, formatMessage({ id: 'cal' }), region);

  let allergenGroups: AllergenGroup[] | undefined;
  if (enableAllergens && product.allergens) {
    allergenGroups = getAllergenGroups(product.allergens, formatMessage);
  }

  const totalFat = `${Math.round(product.nutrition.fat)} ${units.fat}`;
  const carbs = `${Math.round(product.nutrition.carbohydrates)} ${units.carbohydrates}`;
  const sodium = `${Math.round(product.nutrition.sodium)} ${units.sodium}`;

  return (
    <CardContentContainer>
      <CenteredCardTitle>{product.name}</CenteredCardTitle>
      <NutritionInfoRow>
        <NutritionInfoItem name={formatMessage({ id: 'calories' })} value={calories} />
        <NutritionInfoItem name={formatMessage({ id: 'totalFat' })} value={totalFat} />
        <NutritionInfoItem name={formatMessage({ id: 'carbs' })} value={carbs} />
        <NutritionInfoItem name={formatMessage({ id: 'sodium' })} value={sodium} />
      </NutritionInfoRow>
      {allergenGroups?.length && (
        <>
          <AllergenInfoTitle>{formatMessage({ id: 'allergenInformation' })}</AllergenInfoTitle>
          <Allergens allergenGroups={allergenGroups} />
        </>
      )}
    </CardContentContainer>
  );
};

interface ITastySwapModal {
  title: string;
  subtitle: string;
  swap: ISwap;
  backgroundImage?: ISanityImage;
  onDismiss: () => void;
}

export const TastySwapModal: React.FC<ITastySwapModal> = ({
  title,
  subtitle,
  swap,
  backgroundImage,
  onDismiss,
}) => {
  const { selectedProduct, suggestedProduct } = swap;

  return (
    <Modal
      size={ModalSize.REGULAR}
      onDismiss={onDismiss}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Tasty Swap',
      }}
    >
      <Content>
        <Container>
          <Header id="modal-heading">{title}</Header>
          <Subtitle>{subtitle}</Subtitle>
          <CardsContainer>
            <StyledCard
              image={selectedProduct.image}
              imageAlt={selectedProduct.name}
              backgroundImage={backgroundImage}
              imageHeightWidthRatio={0.62}
            >
              <CardContent product={selectedProduct} />
            </StyledCard>
            <StyledCard
              image={suggestedProduct.image}
              imageAlt={suggestedProduct.name}
              backgroundImage={backgroundImage}
              imageHeightWidthRatio={0.62}
            >
              <CardContent product={suggestedProduct} />
            </StyledCard>
          </CardsContainer>
        </Container>
      </Content>
    </Modal>
  );
};
